import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { bizyCommentSuggestionsQueryKey, loadCommentSuggestions, refineCommentSuggestions } from '../../../../../../queries/bizy-comment-suggestions';

export type CommentAssistMode = 'ideas' | 'refine';

export type UseBizyCommentSuggestionsParams = {
	bonusId: string;
	/**
	 * Feature gate (flag + desktop + bizy-enabled). When false the hook is inert
	 * and any open picker is torn down.
	 */
	enabled: boolean;
	/**
	 * Whether this composer currently owns the flying bee. Give me ideas
	 * suggestions persist while the bee is away (so they're still there when it
	 * flies back), whereas Make it shine (refine) is coupled to the live draft —
	 * its draft watcher stops updating once the bee leaves — so it dismisses.
	 */
	isOwner?: boolean;
};

/**
 * Owns Give me ideas + Make it shine sessions: open → fetch 3 suggestions →
 * regenerate (exclude prior) / clear / pick.
 *
 * Threads conversationId so regenerate (and ideas→refine in the same picker
 * session) land as one Trends / Utilization conversation.
 */
export const useBizyCommentSuggestions = ({ bonusId, enabled, isOwner = true }: UseBizyCommentSuggestionsParams) => {
	const [isOpen, setIsOpen] = useState(false);
	const [mode, setMode] = useState<CommentAssistMode>('ideas');
	const [draft, setDraft] = useState('');
	const draftRef = useRef('');
	const conversationIdRef = useRef<string | null>(null);
	// Bumped on dismiss so in-flight fetches cannot restore a conversationId
	// after the user closed the picker (or the bee moved / flag dropped).
	const sessionRef = useRef(0);
	const [exclude, setExclude] = useState<string[]>([]);
	// Bumps on every fetch intent so regenerate still refetches after an error
	// (when there's nothing new to append to `exclude` and the query key would
	// otherwise be unchanged).
	const [requestId, setRequestId] = useState(0);

	const setRefineDraft = useCallback((text: string) => {
		draftRef.current = text;
		setDraft(text);
	}, []);

	const dismiss = useCallback(() => {
		setIsOpen(false);
		setExclude([]);
		setRefineDraft('');
		setMode('ideas');
		conversationIdRef.current = null;
		sessionRef.current += 1;
	}, [setRefineDraft]);

	// The feature gate dropping (flag off / mobile / bizy disabled) tears the
	// picker down entirely.
	useEffect(() => {
		if (!enabled) dismiss();
	}, [enabled, dismiss]);

	// When the bee flies to another box, keep an open Give me ideas picker so it
	// is still there when the bee flies back. Refine is tied to the live draft
	// (its draft watcher stops updating once the bee leaves), so drop it.
	useEffect(() => {
		if (enabled && !isOwner && mode === 'refine') dismiss();
	}, [enabled, isOwner, mode, dismiss]);

	const query = useQuery({
		queryKey: [...bizyCommentSuggestionsQueryKey({ bonusId, exclude, mode }), requestId],
		queryFn: async () => {
			const session = sessionRef.current;
			const conversationId = conversationIdRef.current;
			const result =
				mode === 'refine'
					? await refineCommentSuggestions({ bonusId, draft: draftRef.current, exclude, conversationId })
					: await loadCommentSuggestions({ bonusId, exclude, conversationId });
			if (session === sessionRef.current) {
				conversationIdRef.current ??= result.conversationId ?? null;
			}
			return result;
		},
		enabled: enabled && isOpen && (mode === 'ideas' || draft.trim().length > 0),
		retry: false,
		staleTime: Infinity,
		// Don't retain suggestion sessions (or any lingering draft context) in RQ.
		cacheTime: 0,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const openIdeas = useCallback(() => {
		if (!enabled) return;
		setMode('ideas');
		setRefineDraft('');
		setExclude([]);
		setRequestId((id) => id + 1);
		setIsOpen(true);
	}, [enabled, setRefineDraft]);

	const openRefine = useCallback(
		(draftText: string) => {
			if (!enabled) return;
			const trimmed = draftText.trim();
			if (!trimmed) return;
			setMode('refine');
			setRefineDraft(trimmed);
			setExclude([]);
			setRequestId((id) => id + 1);
			setIsOpen(true);
		},
		[enabled, setRefineDraft]
	);

	/** Optional liveDraft: for refine, refresh the POST body from the current editor. */
	const regenerate = useCallback(
		(liveDraft?: string) => {
			if (mode === 'refine' && liveDraft != null) {
				const trimmed = liveDraft.trim();
				// Editor was cleared while chips were open — dismiss instead of
				// POSTing the snapshot from Make it shine.
				if (!trimmed) {
					dismiss();
					return;
				}
				setRefineDraft(trimmed);
			}
			const current = query.data?.suggestions ?? [];
			setExclude((prev) => [...prev, ...current]);
			setRequestId((id) => id + 1);
		},
		[mode, query.data?.suggestions, setRefineDraft, dismiss]
	);

	return {
		isOpen: enabled && isOpen,
		mode,
		openIdeas,
		openRefine,
		clear: dismiss,
		regenerate,
		suggestions: enabled && isOpen ? (query.data?.suggestions ?? []) : [],
		isLoading: enabled && isOpen && (query.isLoading || query.isFetching),
		isError: enabled && isOpen && query.isError,
		// Exposed so the composer can attribute an adopted suggestion to this
		// conversation. Read it BEFORE calling `clear()` — dismiss nulls it.
		conversationIdRef,
	};
};
